import { ArrowRight, Send } from 'lucide-react';
import api from '../utils/api';

/**
 * ServiceBlock - Reusable component for service detail sections
 * Used in AllServicesPage for each service description
 * 
 * @param {string} id - Section ID for anchor links
 * @param {string} number - Service number (e.g., "01", "02")
 * @param {string} title - Service title
 * @param {string} subtitle - Service subtitle/tagline
 * @param {string} description - Main description text
 * @param {Array} bulletPoints - Array of bullet point strings
 * @param {Object} cta - Call to action { text, href, external, variant }
 * @param {Object} image - Image config { src, alt } or custom content
 * @param {string} accentColor - Color theme: 'yellow', 'cyan', 'purple'
 * @param {string} bgColor - Background: 'dark' (gray-950) or 'light' (gray-900)
 * @param {boolean} imageLeft - Whether image appears on the left
 * @param {React.ReactNode} customImageContent - Custom content instead of image
 */
const ServiceBlock = ({
  id,
  number,
  title,
  subtitle,
  description,
  bulletPoints = [],
  cta,
  ctaInvest,
  image,
  accentColor = 'yellow',
  bgColor = 'dark',
  imageLeft = false,
  customImageContent,
  extraContent
}) => {
  // Track CTA clicks
  const handleInvestClick = (e) => {
    api.trackClick({
      event_type: 'invest_now_whatsapp',
      source: 'services',
      button_id: `invest-${id}`
    });
    // Let the link continue naturally
  };

  const colorMap = {
    yellow: {
      accent: 'text-yellow-500',
      bullet: 'text-yellow-500',
      border: 'border-yellow-500',
      hoverBg: 'hover:bg-yellow-500',
      hoverText: 'hover:text-black',
      imageBorder: 'border-yellow-500/30',
      gradientFrom: 'from-yellow-900/30'
    },
    cyan: {
      accent: 'text-cyan-400',
      bullet: 'text-cyan-400',
      border: 'border-cyan-500',
      hoverBg: 'hover:bg-cyan-500',
      hoverText: 'hover:text-black',
      imageBorder: 'border-cyan-500/20',
      gradientFrom: 'from-cyan-900/30'
    },
    purple: {
      accent: 'text-purple-400',
      bullet: 'text-purple-400',
      border: 'border-purple-500',
      hoverBg: 'hover:bg-purple-500',
      hoverText: 'hover:text-white',
      imageBorder: 'border-purple-500/20',
      gradientFrom: 'from-purple-900/30'
    }
  };

  const colors = colorMap[accentColor] || colorMap.yellow;
  const bgClass = bgColor === 'dark' ? 'bg-gray-950' : 'bg-gray-900';

  const TextContent = () => (
    <div className={imageLeft ? 'order-1 lg:order-2' : ''}>
      <span className={`${colors.accent} text-sm font-mono`}>{number}</span>
      <h2 className="text-3xl font-bold text-white mt-2 mb-4">{title}</h2>
      {subtitle && <p className="text-lg text-gray-400 mb-4">{subtitle}</p>}
      {description && <p className="text-gray-400 mb-6 leading-relaxed">{description}</p>}
      
      {bulletPoints.length > 0 && (
        <ul className="space-y-2 mb-6">
          {bulletPoints.map((point, idx) => (
            <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm">
              <span className={colors.bullet}>•</span> {point}
            </li>
          ))}
        </ul>
      )}

      {extraContent}
      
      <div className="flex flex-wrap gap-3">
        {cta && (
          <a 
            href={cta.href}
            target={cta.external ? "_blank" : undefined}
            rel={cta.external ? "noopener noreferrer" : undefined}
            className={`${colors.border} ${colors.accent} ${colors.hoverBg} ${colors.hoverText} border px-6 py-3 rounded-lg inline-flex items-center font-medium transition-all`}
          >
            {cta.text}
            {cta.icon === 'send' ? <Send className="ml-2" size={16} /> : <ArrowRight className="ml-2" size={16} />}
          </a>
        )}
        {ctaInvest && (
          <a 
            href={ctaInvest.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleInvestClick}
            className="bg-green-600 hover:bg-green-500 text-white px-6 py-3 rounded-lg inline-flex items-center font-medium transition-all"
            data-testid={`invest-btn-${id}`}
          >
            {ctaInvest.text}
            <svg className="ml-2 w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </a>
        )}
      </div>
    </div>
  );

  const ImageContent = () => (
    <div className={imageLeft ? 'order-2 lg:order-1' : ''}>
      {customImageContent ? (
        customImageContent
      ) : image ? (
        image.isCard ? (
          <div className={`bg-gradient-to-br ${colors.gradientFrom} to-gray-900 rounded-xl p-8 border ${colors.imageBorder}`}>
            <img 
              src={image.src}
              alt={image.alt}
              className="w-full rounded-lg"
              loading="lazy"
            />
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl overflow-hidden">
            <img 
              src={image.src}
              alt={image.alt}
              className="w-full rounded-xl"
              loading="lazy"
            />
          </div>
        )
      ) : null}
    </div>
  );

  return (
    <section id={id} className={`py-20 ${bgClass} border-t border-gray-800 scroll-mt-24`}>
      <div className="max-w-5xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {imageLeft ? (
            <>
              <ImageContent />
              <TextContent />
            </>
          ) : (
            <>
              <TextContent />
              <ImageContent />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default ServiceBlock;
